import { AppContext } from 'App.context';
import { useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import constants from 'utils/constants';

const useDisableLicenceFormHook = () => {
  const {
    appState,
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);

  const { API } = useContext(ApiContext);

  const reasons = [
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.NOTNEEDED}`
      ),
      value: constants.LICENCE_END_REASONS.NOTNEEDED,
    },
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.HIGHCOST}`
      ),
      value: constants.LICENCE_END_REASONS.HIGHCOST,
    },
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.MISSINGFEATURES}`
      ),
      value: constants.LICENCE_END_REASONS.MISSINGFEATURES,
    },
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.TECHNICALISSUES}`
      ),
      value: constants.LICENCE_END_REASONS.TECHNICALISSUES,
    },
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.POORSUPPORT}`
      ),
      value: constants.LICENCE_END_REASONS.POORSUPPORT,
    },
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.LOWADOPTION}`
      ),
      value: constants.LICENCE_END_REASONS.LOWADOPTION,
    },
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.SWITCHEDPROVIDER}`
      ),
      value: constants.LICENCE_END_REASONS.SWITCHEDPROVIDER,
    },
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.TOOCOMPLEX}`
      ),
      value: constants.LICENCE_END_REASONS.TOOCOMPLEX,
    },
    {
      label: translate(
        `accountDetail.disableLicence.reasonStep.reasons.${constants.LICENCE_END_REASONS.OTHER}`
      ),
      value: constants.LICENCE_END_REASONS.OTHER,
    },
  ];

  const [state, setState] = useState({
    step: 0,
    selectedReasons: [],
    selectedHotelLicences: [],
    otherReason: '',
    selectedHotel: getSelectedHotel() || null,
    hotelLicences: [],
    selectedLicencesWithHotelId: {},
    selectedLicencesMetaWithHotelId: {},
    isAllHotelsSelected: false,
    isLicencesLoading: false,
    isAllServicesCheckbox: false,
    fetchLicencesError: false,
    isConfirmLoading: false,
  });

  function resetForm() {
    setTimeout(() => {
      updateState({
        step: 0,
        selectedReasons: [],
        selectedHotelLicences: [],
        otherReason: '',
        selectedHotel: getSelectedHotel() || null,
        hotelLicences: [],
        selectedLicencesWithHotelId: {},
        selectedLicencesMetaWithHotelId: {},
        isAllHotelsSelected: false,
        isLicencesLoading: false,
        isAllServicesCheckbox: false,
        fetchLicencesError: false,
        isConfirmLoading: false,
      });
    }, 1000);
  }

  useEffect(() => {
    if (state.step === 1) {
      actions.handleHotelSelected(state.selectedHotel);
    }
  }, [state.step]);

  const actions = {
    setStep: step => {
      updateState({ step });
    },
    handleReasonsChange: checkedValues => {
      const other = checkedValues.includes('other') ? state.otherReason : '';
      updateState({ selectedReasons: checkedValues, otherReason: other });
    },
    handleHotelLicencesChange: checkedValues => {
      const hotelId = state.selectedHotel?.id;
      const updatedSelectedLicencesWithHotelId = { ...(state.selectedLicencesWithHotelId || {}) };
      const updatedSelectedLicencesMetaWithHotelId = {
        ...(state.selectedLicencesMetaWithHotelId || {}),
      };

      if (hotelId) {
        if (checkedValues.length > 0) {
          updatedSelectedLicencesWithHotelId[hotelId] = checkedValues;
          const metaMap = {};
          (state.hotelLicences || []).forEach(l => {
            if (checkedValues.includes(l.id)) {
              metaMap[l.id] = { id: l.id, name: l.name };
            }
          });
          if (Object.keys(metaMap).length > 0) {
            updatedSelectedLicencesMetaWithHotelId[hotelId] = metaMap;
          } else {
            if (!updatedSelectedLicencesMetaWithHotelId[hotelId]) {
              updatedSelectedLicencesMetaWithHotelId[hotelId] = {};
            }
          }
        } else {
          delete updatedSelectedLicencesWithHotelId[hotelId];
          delete updatedSelectedLicencesMetaWithHotelId[hotelId];
        }
      }

      updateState({
        selectedHotelLicences: checkedValues,
        selectedLicencesWithHotelId: updatedSelectedLicencesWithHotelId,
        selectedLicencesMetaWithHotelId: updatedSelectedLicencesMetaWithHotelId,
      });
    },
    handleOtherReasonChange: e => {
      updateState({ otherReason: e.target.value });
    },
    handleContinuar: () => {
      updateState({ step: state.step + 1 });
    },
    handleBack: () => {
      updateState({ step: state.step - 1 });
    },
    handleConfirm: () => {
      updateState({ isConfirmLoading: true });

      const updateData = {
        reasons: state.selectedReasons.join(';'),
        otherReason: state.otherReason,
        hotelsWithLicences: state.selectedLicencesWithHotelId,
        status: constants.LICENCE_END_STATUS.REQUESTED,
        isAllHotelsSelected: state.isAllHotelsSelected,
        allHotels: appState.hotelList.map(hotel => hotel.id),
      };

      API.updateHotelLicenceEnd(updateData)
        .then(res => {
          actions.setStep(4);
        })
        .catch(err => {
          actions.setStep(3);
        })
        .finally(() => {
          updateState({ isConfirmLoading: false });
        });
    },
    handleAllServicesCheckbox: e => {
      updateState({ isAllServicesCheckbox: e.target.checked });
    },
    handleHotelSelected: hotel => {
      if (!hotel.id && !hotel.key) {
        updateState({ isAllHotelsSelected: true });
      } else {
        updateState({ isLicencesLoading: true });

        API.getHotelLicencesList(hotel.id)
          .then(response => {
            const licencesList = response.items.filter(
              licence => licence.active && licence.type !== 'oneshot'
            );

            const selectedHotelLicences = (state.selectedLicencesWithHotelId || {})[hotel.id] || [];

            updateState({
              isAllHotelsSelected: false,
              hotelLicences: licencesList,
              selectedHotelLicences,
              selectedHotel: hotel,
              fetchLicencesError: false,
            });
          })
          .catch(err => {
            updateState({ fetchLicencesError: true });
          })
          .finally(() => {
            updateState({ isLicencesLoading: false });
          });
      }
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions, reasons, resetForm };
};

export default useDisableLicenceFormHook;
